import React, { useState } from 'react';
import { SearchOutlined, QuestionCircleOutlined, UserOutlined, ShoppingOutlined, DollarOutlined, SafetyOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

// Custom styles for accordion
const accordionButtonStyles = `
  .accordion-button:not(.collapsed) {
    background: linear-gradient(135deg, rgb(13, 110, 253) 0%, rgb(0, 80, 179) 100%) !important;
    color: #fff !important;
    box-shadow: none !important;
  }
  .accordion-button:not(.collapsed)::after {
    filter: brightness(0) invert(1);
  }
`;

const FAQ = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeKey, setActiveKey] = useState('1');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const allFaqItems = [
    {
      key: '1',
      label: 'How do I register for an account?',
      children: 'To register, click on the "Register" button in the top right corner of the page. Fill in your personal information including your first name, last name, email address, phone number, and create a secure password. Once you submit the registration form, your account will be pending admin approval. You will need to wait for admin approval before you can fully use the platform.',
      category: 'account',
      icon: <UserOutlined />,
    },
    {
      key: '2',
      label: 'Do I need separate accounts for buying and selling?',
      children: 'No! One account is all you need. Once your account is approved, you can both buy and sell using the same account. You can create auctions to sell items and also bid on other auctions to buy items - all from one dashboard.',
      category: 'account',
      icon: <UserOutlined />,
    },
    {
      key: '3',
      label: 'How do I participate in an auction?',
      children: 'First, ensure your account is approved and you are logged in. Browse available auctions from the "Auctions" or "Live Auctions" pages. Select an auction you\'re interested in and review the lots. For public auctions, you can bid immediately. For private auctions, you\'ll need to be invited by the auction owner. Place your bids on items you want, and track them in real-time.',
      category: 'bidding',
      icon: <ShoppingOutlined />,
    },
    {
      key: '4',
      label: 'What is the difference between live auctions and scheduled auctions?',
      children: 'Live auctions are currently active and happening in real-time. You can watch and bid on lots as they become available. Scheduled auctions are set to start at a specific time in the future. Sequential auctions allow lots to be bid on one at a time in order, while non-sequential auctions allow bidding on all lots simultaneously.',
      category: 'bidding',
      icon: <ShoppingOutlined />,
    },
    {
      key: '5',
      label: 'What are private auctions?',
      children: 'Private auctions are invitation-only auctions. Only users who have been invited by the auction owner can participate. If you receive an invitation, you can accept or decline it. Once accepted, you\'ll be able to bid on lots in that private auction.',
      category: 'bidding',
      icon: <ShoppingOutlined />,
    },
    {
      key: '6',
      label: 'How do I create an auction?',
      children: 'After your account is approved, navigate to "My Auctions" from your dashboard and click "Create Auction". Fill in the auction details including title, description, category, start time, location, and configure settings like whether it\'s private or sequential. You can also upload images and add a video URL. Once created, you can add lots (items) to your auction.',
      category: 'selling',
      icon: <ShoppingOutlined />,
    },
    {
      key: '7',
      label: 'What happens if I win an auction?',
      children: 'If you win a lot, you will be notified in real-time during the auction and via email. The notification will also appear in your dashboard. You\'ll need to complete the payment within the specified timeframe. Once payment is confirmed, arrangements will be made for item pickup or delivery according to the auction terms.',
      category: 'bidding',
      icon: <DollarOutlined />,
    },
    {
      key: '8',
      label: 'Can I cancel a bid?',
      children: 'Bids are generally final and cannot be cancelled once placed. However, if you believe you made an error, contact our support team immediately. We will review your request on a case-by-case basis, but cancellation is not guaranteed.',
      category: 'bidding',
      icon: <QuestionCircleOutlined />,
    },
    {
      key: '9',
      label: 'What is auto-bid?',
      children: 'Auto-bid is a feature that automatically places bids for you up to your maximum bid amount. When you enable auto-bid on a lot, the system will automatically outbid competitors until your maximum is reached. This helps ensure you don\'t miss out on items you really want.',
      category: 'bidding',
      icon: <ShoppingOutlined />,
    },
    {
      key: '10',
      label: 'What are reserve prices?',
      children: 'A reserve price is the minimum price a seller is willing to accept for a lot. If bidding doesn\'t reach the reserve price, the lot may not be sold. Reserve prices are not visible to bidders, but the system will indicate if a reserve has been met.',
      category: 'selling',
      icon: <DollarOutlined />,
    },
    {
      key: '11',
      label: 'What payment methods are accepted?',
      children: 'Payment processing is currently being set up. Accepted payment methods will be displayed during the checkout process once you win an auction. All transactions will be processed securely. For specific payment options, please contact support.',
      category: 'payment',
      icon: <DollarOutlined />,
    },
    {
      key: '12',
      label: 'How do I track my bids and auctions?',
      children: 'You can track all your activity from your dashboard. View your bid history, winning bids, auctions you\'ve created, and lots you\'re selling. You\'ll also receive real-time notifications when you\'re outbid, when you win, or when your auctions receive bids. Check the "Analytics" section for detailed statistics.',
      category: 'account',
      icon: <UserOutlined />,
    },
    {
      key: '13',
      label: 'What are the fees for using the platform?',
      children: 'Fee structure details are being finalized. Please refer to our Terms and Conditions or contact support for specific information about platform fees, commissions, and any applicable charges.',
      category: 'payment',
      icon: <DollarOutlined />,
    },
    {
      key: '14',
      label: 'What if I have a dispute?',
      children: 'If you encounter any issues or disputes, you can file a dispute through the "Disputes" section in your dashboard or from the main navigation. Our support team will review your case and work to resolve it fairly. You can track the status of your dispute in your account.',
      category: 'support',
      icon: <SafetyOutlined />,
    },
    {
      key: '15',
      label: 'How long does account approval take?',
      children: 'Account approval is typically processed by our admin team. The timeframe may vary, but you can check your approval status in your account. If your account is pending for an extended period, please contact support for assistance.',
      category: 'account',
      icon: <UserOutlined />,
    },
  ];

  const categories = [
    { key: 'all', label: 'All Categories', count: allFaqItems.length },
    { key: 'account', label: 'Account', count: allFaqItems.filter(item => item.category === 'account').length },
    { key: 'bidding', label: 'Bidding', count: allFaqItems.filter(item => item.category === 'bidding').length },
    { key: 'selling', label: 'Selling', count: allFaqItems.filter(item => item.category === 'selling').length },
    { key: 'payment', label: 'Payment', count: allFaqItems.filter(item => item.category === 'payment').length },
    { key: 'support', label: 'Support', count: allFaqItems.filter(item => item.category === 'support').length },
  ];

  const filteredFaqItems = allFaqItems.filter(item => {
    const matchesSearch = item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.children.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleAccordion = (key) => {
    setActiveKey(activeKey === key ? null : key);
  };

  const getCategoryStyle = (category) => {
    const categoryStyles = {
      'account': {
        backgroundColor: '#e7f1ff',
        color: '#0d6efd',
        borderColor: '#b3d9ff'
      },
      'bidding': {
        backgroundColor: '#d1e7dd',
        color: '#198754',
        borderColor: '#a3cfbb'
      },
      'selling': {
        backgroundColor: '#cff4fc',
        color: '#0dcaf0',
        borderColor: '#9eeaf9'
      },
      'payment': {
        backgroundColor: '#fff3cd',
        color: '#ffc107',
        borderColor: '#ffe69c'
      },
      'support': {
        backgroundColor: '#f8d7da',
        color: '#dc3545',
        borderColor: '#f1aeb5'
      },
    };
    return categoryStyles[category] || {
      backgroundColor: '#e9ecef',
      color: '#6c757d',
      borderColor: '#ced4da'
    };
  };

  const primaryBlue = '#0d6efd';
  const darkBlue = '#0050b3';
  const lightBlue = '#4096ff';

  return (
    <>
      <style>{accordionButtonStyles}</style>
      <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh', paddingTop: '40px', paddingBottom: '80px' }} className="faq-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-9">
              {/* Professional Header Section */}
              <div className="mb-5" style={{ paddingTop: '40px' }}>
                <div className="text-center mb-5">
                  <div className="d-flex flex-column flex-md-row align-items-center justify-content-center mb-4 faq-header-section">
                    <div
                      className="faq-header-icon mb-3 mb-md-0"
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '24px',
                        background: 'linear-gradient(135deg, rgb(13, 110, 253) 0%, rgb(0, 80, 179) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 12px 32px rgba(13, 110, 253, 0.4)',
                        marginRight: '0',
                        transition: 'all 0.3s ease',
                        animation: 'pulse 2s infinite'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
                        e.currentTarget.style.boxShadow = '0 16px 40px rgba(13, 110, 253, 0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                        e.currentTarget.style.boxShadow = '0 12px 32px rgba(13, 110, 253, 0.4)';
                      }}
                    >
                      <QuestionCircleOutlined className="faq-header-icon-svg" style={{ fontSize: '50px', color: '#fff' }} />
                    </div>
                    <div className="text-center text-md-start ms-md-3">
                      <h1
                        className="fw-bold mb-2 faq-main-title"
                        style={{
                          background: 'linear-gradient(135deg, rgb(13, 110, 253) 0%, rgb(0, 80, 179) 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          letterSpacing: '-0.8px',
                          fontSize: '3rem',
                          margin: 0,
                          lineHeight: '1.2'
                        }}
                      >
                        Frequently Asked Questions
                      </h1>
                      <p
                        className="text-muted mb-0 faq-subtitle"
                        style={{
                          fontSize: '1.2rem',
                          lineHeight: '1.6',
                          color: '#6c757d',
                          marginTop: '8px'
                        }}
                      >
                        Find answers to common questions
                      </p>
                    </div>
                  </div>
                  <p
                    className="mt-4 mb-5 faq-description"
                    style={{
                      fontSize: '1.15rem',
                      lineHeight: '1.8',
                      maxWidth: '800px',
                      margin: '24px auto 0',
                      color: '#495057'
                    }}
                  >
                    Search or browse by category to find answers about using our auction platform.
                  </p>
                </div>
              </div>

              {/* Professional Search Bar */}
              <div className="mb-5">
                <div
                  className="card border-0 shadow-lg position-relative overflow-hidden faq-search-card"
                  style={{
                    borderRadius: '24px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    border: '3px solid rgba(13, 110, 253, 0.15)',
                    transition: 'all 0.3s ease',
                    padding: '40px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(13, 110, 253, 0.15)';
                    e.currentTarget.style.borderColor = 'rgba(13, 110, 253, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.borderColor = 'rgba(13, 110, 253, 0.15)';
                  }}
                >
                  {/* Decorative top border */}
                  <div
                    className="position-absolute top-0 start-0 w-100"
                    style={{
                      height: '6px',
                      background: 'linear-gradient(90deg, rgb(13, 110, 253) 0%, rgb(0, 80, 179) 100%)',
                      borderRadius: '24px 24px 0 0'
                    }}
                  />

                  {/* Decorative corner element */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '-40px',
                      right: '-40px',
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      background: 'rgba(13, 110, 253, 0.05)',
                      transition: 'all 0.4s ease'
                    }}
                  />

                  <div className="position-relative">
                    <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start mb-3 faq-search-header">
                      <div
                        className="faq-search-icon mb-3 mb-md-0"
                        style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '14px',
                          background: 'linear-gradient(135deg, rgb(13, 110, 253) 0%, rgb(0, 80, 179) 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '0',
                          boxShadow: '0 6px 20px rgba(13, 110, 253, 0.3)',
                          flexShrink: 0
                        }}
                      >
                        <SearchOutlined style={{ fontSize: '28px', color: '#fff' }} />
                      </div>
                      <h3
                        className="fw-bold mb-0 faq-search-title text-center text-md-start ms-md-3"
                        style={{
                          color: '#212529',
                          fontSize: '1.5rem',
                          letterSpacing: '-0.3px'
                        }}
                      >
                        Search FAQs
                      </h3>
                    </div>
                    <div className="input-group input-group-lg faq-search-input-group">
                      <span
                        className="input-group-text border-0 faq-search-input-icon"
                        style={{
                          background: 'rgba(13, 110, 253, 0.05)',
                          borderTopLeftRadius: '16px',
                          borderBottomLeftRadius: '16px',
                          paddingLeft: '24px',
                          paddingRight: '20px'
                        }}
                      >
                        <SearchOutlined className="faq-search-icon-inner" style={{ fontSize: '20px', color: primaryBlue }} />
                      </span>
                      <input
                        type="text"
                        className="form-control border-0 faq-search-input"
                        placeholder="Type your question here..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                          fontSize: '1.1rem',
                          padding: '16px 20px',
                          background: 'rgba(13, 110, 253, 0.02)',
                          borderTopRightRadius: '16px',
                          borderBottomRightRadius: '16px'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.background = 'rgba(13, 110, 253, 0.05)';
                          e.currentTarget.style.outline = 'none';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.background = 'rgba(13, 110, 253, 0.02)';
                        }}
                      />
                    </div>
                    {searchQuery && (
                      <p className="text-muted mb-0 mt-3" style={{ fontSize: '0.95rem' }}>
                        Found {filteredFaqItems.length} result{filteredFaqItems.length !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Category Filter */}
              <div className="mb-4">
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  {categories.map(cat => (
                    <button
                      key={cat.key}
                      className={`btn ${selectedCategory === cat.key ? 'btn-primary' : 'btn-outline-primary'} position-relative`}
                      onClick={() => setSelectedCategory(cat.key)}
                      style={{ borderRadius: '20px', paddingLeft: '20px', paddingRight: '20px' }}
                    >
                      {cat.label}
                      <span
                        className={`badge rounded-pill ms-2 ${selectedCategory === cat.key ? 'bg-white text-primary' : 'bg-primary text-white'}`}
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          padding: '4px 8px',
                          minWidth: '24px'
                        }}
                      >
                        {cat.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* FAQ Accordion */}
              {filteredFaqItems.length > 0 ? (
                <div className="accordion mb-5" id="faqAccordion">
                  {filteredFaqItems.map((item, index) => (
                    <div key={item.key} className="accordion-item border-0 shadow-sm mb-3" style={{ borderRadius: '12px' }}>
                      <h2 className="accordion-header" id={`heading${item.key}`}>
                        <button
                          className={`accordion-button ${activeKey === item.key ? '' : 'collapsed'}`}
                          type="button"
                          onClick={() => toggleAccordion(item.key)}
                          style={{
                            borderRadius: activeKey === item.key ? '12px 12px 0 0' : '12px',
                            fontWeight: '500',
                            fontSize: '1.05rem',
                            background: activeKey === item.key
                              ? 'linear-gradient(135deg, rgb(13, 110, 253) 0%, rgb(0, 80, 179) 100%)'
                              : 'transparent',
                            color: activeKey === item.key ? '#fff' : 'inherit',
                            border: 'none',
                            boxShadow: activeKey === item.key ? 'none' : 'inherit',
                          }}
                        >
                          <div className="d-flex align-items-center w-100 me-3">
                            <span
                              className="me-3"
                              style={{
                                color: activeKey === item.key ? '#fff' : '#0d6efd',
                                fontSize: '20px'
                              }}
                            >
                              {item.icon}
                            </span>
                            <span className="flex-grow-1 text-start">{item.label}</span>
                            <span
                              className="badge rounded-pill ms-2 text-uppercase faq-category-badge"
                              style={{
                                ...(activeKey === item.key
                                  ? {
                                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                    color: '#fff',
                                    border: '1px solid rgba(255, 255, 255, 0.4)'
                                  }
                                  : getCategoryStyle(item.category)),
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                padding: '5px 12px',
                                letterSpacing: '0.5px',
                                ...(activeKey !== item.key && {
                                  border: `1px solid ${getCategoryStyle(item.category).borderColor}`
                                })
                              }}
                            >
                              {item.category}
                            </span>
                          </div>
                        </button>
                      </h2>
                      {activeKey === item.key && (
                        <div
                          id={`collapse${item.key}`}
                          className="accordion-collapse collapse show"
                          data-bs-parent="#faqAccordion"
                        >
                          <div className="accordion-body pt-4 pb-4" style={{ fontSize: '1rem', lineHeight: '1.7', color: '#495057' }}>
                            {item.children}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card shadow-sm border-0 text-center py-5 mb-5" style={{ borderRadius: '16px' }}>
                  <div className="card-body">
                    <QuestionCircleOutlined style={{ fontSize: '64px', color: '#dee2e6', marginBottom: '20px' }} />
                    <h4 className="text-muted mb-3">No results found</h4>
                    <p className="text-muted mb-0">
                      Try adjusting your search terms or select a different category.
                    </p>
                  </div>
                </div>
              )}

              {/* Support Card */}
              <div className="card border-0 shadow-lg mb-4 faq-support-card"
                style={{
                  background: 'linear-gradient(135deg, #f0f7ff 0%, #e6f7ff 50%, #bae7ff 100%)',
                  border: '2px solid #91d5ff !important',
                  borderRadius: '16px'
                }}>
                <div className="card-body text-center p-5 faq-support-body">
                  <CustomerServiceOutlined className="faq-support-icon" style={{ fontSize: '56px', color: '#0d6efd', marginBottom: '20px' }} />
                  <h3 className="fw-bold mb-3 faq-support-title" style={{ color: '#0d6efd' }}>Still have questions?</h3>
                  <p className="lead mb-4 faq-support-text" style={{ color: '#495057' }}>
                    If you couldn't find the answer you're looking for, please don't hesitate to contact our support team.
                  </p>
                  <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mb-4 faq-support-contact">
                    <div className="d-flex align-items-center justify-content-center">
                      <strong className="me-2">Email:</strong>
                      <a href="mailto:contact@maveshibids.com" className="text-decoration-none">
                        contact@maveshibids.com
                      </a>
                    </div>
                    <div className="d-flex align-items-center justify-content-center">
                      <strong className="me-2">Phone:</strong>
                      <span>+923281419656</span>
                    </div>
                  </div>
                  <button
                    className="btn btn-primary btn-lg px-4 faq-support-button"
                    onClick={() => navigate('/feedback')}
                    style={{ borderRadius: '25px' }}
                  >
                    Contact Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
            }
          }

          /* Responsive Styles */
          .faq-page {
            padding-top: 20px !important;
            padding-bottom: 40px !important;
          }

          /* Header Section - Mobile */
          .faq-header-section {
            flex-direction: column !important;
            text-align: center !important;
          }

          .faq-header-icon {
            margin-right: 0 !important;
            margin-bottom: 15px !important;
            width: 80px !important;
            height: 80px !important;
          }

          .faq-header-icon-svg {
            font-size: 40px !important;
          }

          .faq-main-title {
            font-size: 2rem !important;
            text-align: center !important;
          }

          .faq-subtitle {
            font-size: 1rem !important;
            text-align: center !important;
          }

          .faq-description {
            font-size: 1rem !important;
            padding: 0 15px;
          }

          /* Search Card - Mobile */
          .faq-search-card {
            padding: 24px !important;
          }

          .faq-search-header {
            flex-direction: column !important;
            align-items: center !important;
          }

          .faq-search-icon {
            margin-right: 0 !important;
            margin-bottom: 15px !important;
          }

          .faq-search-title {
            text-align: center !important;
            font-size: 1.3rem !important;
          }

          /* Category Buttons - Mobile */
          .btn {
            font-size: 0.9rem !important;
            padding: 8px 16px !important;
          }

          /* Search Input - Mobile */
          .faq-search-input-group {
            flex-wrap: nowrap !important;
          }

          .faq-search-input-icon {
            padding-left: 16px !important;
            padding-right: 12px !important;
            flex-shrink: 0 !important;
          }

          .faq-search-icon-inner {
            font-size: 18px !important;
          }

          .faq-search-input {
            font-size: 1rem !important;
            padding: 12px 16px !important;
          }

          .faq-search-input::placeholder {
            font-size: 0.9rem !important;
          }

          /* Hide category badge in accordion on mobile */
          .faq-category-badge {
            display: none !important;
          }

          /* Accordion - Mobile */
          .accordion-button {
            font-size: 0.95rem !important;
            padding: 16px 20px !important;
          }

          .accordion-body {
            font-size: 0.9rem !important;
            padding: 16px 20px !important;
          }

          /* Support Card - Mobile */
          .faq-support-card {
            border-radius: 16px !important;
          }

          .faq-support-body {
            padding: 30px 20px !important;
          }

          .faq-support-icon {
            font-size: 48px !important;
          }

          .faq-support-title {
            font-size: 1.5rem !important;
          }

          .faq-support-text {
            font-size: 1rem !important;
          }

          .faq-support-button {
            font-size: 1rem !important;
            padding: 12px 32px !important;
          }

          /* Tablet and up */
          @media (min-width: 768px) {
            .faq-page {
              padding-top: 40px !important;
              padding-bottom: 60px !important;
            }

            .faq-header-section {
              flex-direction: row !important;
              text-align: left !important;
            }

            .faq-header-icon {
              margin-right: 24px !important;
              margin-bottom: 0 !important;
              width: 100px !important;
              height: 100px !important;
            }

            .faq-header-icon-svg {
              font-size: 50px !important;
            }

            .faq-main-title {
              font-size: 2.5rem !important;
              text-align: left !important;
            }

            .faq-subtitle {
              font-size: 1.1rem !important;
              text-align: left !important;
            }

            .faq-description {
              font-size: 1.1rem !important;
              padding: 0;
            }

            .faq-search-card {
              padding: 32px !important;
            }

            .faq-search-header {
              flex-direction: row !important;
              align-items: flex-start !important;
            }

            .faq-search-icon {
              margin-right: 20px !important;
              margin-bottom: 0 !important;
            }

            .faq-search-title {
              text-align: left !important;
              font-size: 1.4rem !important;
            }

            /* Search Input - Tablet */
            .faq-search-input-icon {
              padding-left: 24px !important;
              padding-right: 20px !important;
            }

            .faq-search-icon-inner {
              font-size: 20px !important;
            }

            .faq-search-input {
              font-size: 1.1rem !important;
              padding: 16px 20px !important;
            }

            .faq-search-input::placeholder {
              font-size: 1rem !important;
            }

            /* Show category badge in accordion on tablet and up */
            .faq-category-badge {
              display: inline-block !important;
            }

            .accordion-button {
              font-size: 1.05rem !important;
            }

            .accordion-body {
              font-size: 1rem !important;
            }

            .faq-support-body {
              padding: 40px !important;
            }

            .faq-support-icon {
              font-size: 56px !important;
            }

            .faq-support-title {
              font-size: 1.75rem !important;
            }

            .faq-support-text {
              font-size: 1.1rem !important;
            }
          }

          /* Desktop and up */
          @media (min-width: 992px) {
            .faq-main-title {
              font-size: 3rem !important;
            }

            .faq-subtitle {
              font-size: 1.2rem !important;
            }

            .faq-description {
              font-size: 1.15rem !important;
            }

            .faq-search-card {
              padding: 40px !important;
            }

            .faq-search-title {
              font-size: 1.5rem !important;
            }

            /* Search Input - Desktop */
            .faq-search-input-icon {
              padding-left: 24px !important;
              padding-right: 20px !important;
            }

            .faq-search-icon-inner {
              font-size: 20px !important;
            }

            .faq-search-input {
              font-size: 1.1rem !important;
              padding: 16px 20px !important;
            }

            .faq-support-body {
              padding: 50px !important;
            }

            .faq-support-title {
              font-size: 2rem !important;
            }
          }

          /* Extra small devices */
          @media (max-width: 575px) {
            .faq-page {
              padding-top: 15px !important;
              padding-bottom: 30px !important;
            }

            .faq-header-icon {
              width: 70px !important;
              height: 70px !important;
            }

            .faq-header-icon-svg {
              font-size: 35px !important;
            }

            .faq-main-title {
              font-size: 1.75rem !important;
            }

            .faq-subtitle {
              font-size: 0.9rem !important;
            }

            .faq-description {
              font-size: 0.9rem !important;
              padding: 0 10px;
            }

            .faq-search-card {
              padding: 20px !important;
            }

            .faq-search-icon {
              width: 48px !important;
              height: 48px !important;
            }

            .faq-search-title {
              font-size: 1.2rem !important;
            }

            /* Search Input - Extra Small */
            .faq-search-input-icon {
              padding-left: 12px !important;
              padding-right: 10px !important;
            }

            .faq-search-icon-inner {
              font-size: 16px !important;
            }

            .faq-search-input {
              font-size: 0.9rem !important;
              padding: 10px 12px !important;
            }

            .faq-search-input::placeholder {
              font-size: 0.85rem !important;
            }

            .btn {
              font-size: 0.85rem !important;
              padding: 6px 12px !important;
            }

            /* Hide category badge in accordion on extra small devices */
            .faq-category-badge {
              display: none !important;
            }

            .accordion-button {
              font-size: 0.9rem !important;
              padding: 14px 16px !important;
            }

            .accordion-body {
              font-size: 0.85rem !important;
              padding: 14px 16px !important;
            }

            .faq-support-body {
              padding: 24px 16px !important;
            }

            .faq-support-icon {
              font-size: 40px !important;
            }

            .faq-support-title {
              font-size: 1.3rem !important;
            }

            .faq-support-text {
              font-size: 0.95rem !important;
            }

            .faq-support-button {
              font-size: 0.95rem !important;
              padding: 10px 24px !important;
            }
          }

          /* Small devices (landscape phones) */
          @media (min-width: 576px) and (max-width: 767px) {
            .faq-header-section {
              flex-direction: row !important;
              text-align: left !important;
            }

            .faq-header-icon {
              margin-right: 20px !important;
              margin-bottom: 0 !important;
            }

            .faq-main-title {
              text-align: left !important;
            }

            .faq-subtitle {
              text-align: left !important;
            }

            .faq-search-header {
              flex-direction: row !important;
              align-items: flex-start !important;
            }

            .faq-search-icon {
              margin-right: 15px !important;
              margin-bottom: 0 !important;
            }

            .faq-search-title {
              text-align: left !important;
            }

            /* Search Input - Small */
            .faq-search-input-icon {
              padding-left: 20px !important;
              padding-right: 16px !important;
            }

            .faq-search-icon-inner {
              font-size: 19px !important;
            }

            .faq-search-input {
              font-size: 1.05rem !important;
              padding: 14px 18px !important;
            }

            /* Show category badge in accordion on small devices */
            .faq-category-badge {
              display: inline-block !important;
            }
          }
        `}</style>
    </>
  );
};

export default FAQ;

